import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { timer } from 'rxjs';
import { MatBadge } from '@angular/material/badge';
import { ChatService } from '@app/shared/chat/chat.service';
import { WorkflowRunsHubService } from '@app/shared/chat/workflow-runs-hub.service';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Event, Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ShellLoaderService } from './shell-loader.service';
import { ShellSidenavService } from './shell-sidenav.service';
import { StingrayAuthenticationService } from '@app/core/authentication/stingray-authentication.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { TranslateModule } from '@ngx-translate/core';
import { MatDivider } from '@angular/material/divider';
import { MatList, MatListItem } from '@angular/material/list';
import { HeaderComponent } from './header/header.component';
import { ChatWindowComponent } from '@app/shared/chat/chat-window.component';

@Component({
    selector: 'unite-shell',
    templateUrl: './shell.component.html',
    styleUrls: ['./shell.component.scss'],
    imports: [HeaderComponent, ChatWindowComponent, MatSidenavContainer, MatSidenav, MatList, MatDivider, MatListItem, RouterLink, RouterLinkActive, TranslateModule, MatSidenavContent, MatProgressSpinner, RouterOutlet, MatFabButton, MatIcon, MatTooltip, MatBadge]
})
export class ShellComponent implements OnInit {

  // The documents the assistant has prepared (and waits on an answer for) are pushed by the agent's hub; this slow
  // refresh only covers what a dropped connection missed.
  private static readonly pendingRunsFallbackPollMs = 5 * 60_000;

  private destroyRef = inject(DestroyRef);
  protected chatService = inject(ChatService);
  private workflowRunsHub = inject(WorkflowRunsHubService);
  @ViewChild('sidenav', { static: true }) sidenav!: MatSidenav;
  @ViewChild('chatSidenav', { static: true }) chatSidenav!: MatSidenav;

  mobileQuery: MediaQueryList;
  loading = false;
  requestLoader: boolean;
  isProfessional = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private shellLoaderService: ShellLoaderService,
    private shellSidenavService: ShellSidenavService,
    private stingrayAuthenticationService: StingrayAuthenticationService) {
  }

  ngOnInit(): void {
    this.shellSidenavService.setGeneralSidenav(this.sidenav);
    this.shellSidenavService.setChatSidenav(this.chatSidenav);

    // Automatically close sidenavas on screens > sm breakpoint
    this.breakpointObserver.observe([
      Breakpoints.XSmall, // xs
      Breakpoints.Small   // sm
    ])
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(result => {
      // result.matches is true als het scherm 'xs' of 'sm' IS.
      // We willen de sidenav sluiten als het scherm NIET 'xs' of 'sm' is (dus groter).
      if (!result.matches) {
        this.shellSidenavService.closeGeneralSidenav();
      }

      // De linker sidenav moet blijkbaar altijd open (volgens je originele code)
      this.shellSidenavService.openLeftSidenav();
    });

    this.router.events.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((routerEvent: Event) => {
      this.checkRouterEvent(routerEvent);
    });

    this.shellLoaderService.getRequestLoader().subscribe((requestLoader: boolean) => {
      this.requestLoader = requestLoader;
    });

    this.isProfessional = this.stingrayAuthenticationService.organization.subscription === 'Professional';

    // The badge on the assistant button counts the documents waiting for the user's answer: loaded on connect and
    // on every change the agent pushes, with a slow refresh as fallback.
    this.workflowRunsHub.connect();
    timer(ShellComponent.pendingRunsFallbackPollMs, ShellComponent.pendingRunsFallbackPollMs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.chatService.loadPendingRuns());
  }

  protected toggleChatSideNav(): void {
    this.shellSidenavService.toggleChatSidenav();
  }

  checkRouterEvent(routerEvent: Event): void {
    // Navigation comes to an end
    if (routerEvent instanceof NavigationStart) {
      if (this.shellSidenavService.getLeftSidenav() !== null) {
        this.shellLoaderService.setCustomerContentLoader(true);
      } else {
        this.loading = true;
      }
    }

    // Navigation comes to an end
    if (routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError) {
      if (this.shellSidenavService.getLeftSidenav() !== null) {
        this.shellLoaderService.setCustomerContentLoader(false);
      } else {
        this.loading = false;
      }
    }
  }
}
